import axios from "axios";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// Auth
export const authApi = {
  login: (phone, password, ukcNumber) => API.post("/auth/login", { phone, password, ukcNumber: ukcNumber || undefined }),
  verifyOtp: (pendingToken, code, trustDevice = true) => API.post("/auth/verify-otp", { pendingToken, code, trustDevice }),
  resendOtp: (pendingToken, channel) => API.post("/auth/resend-otp", { pendingToken, channel }),
  register: (name, phone, password) => API.post("/auth/register", { name, phone, password }),
  logout: () => API.post("/auth/logout"),
  me: () => API.get("/auth/me"),
  refresh: () => API.post("/auth/refresh"),
  googleSession: (sessionId) => API.post("/auth/google/session", { session_id: sessionId }),
  forgotPassword: (phone) => API.post("/auth/forgot-password", { phone }),
  resetPassword: (token, newPassword) => API.post("/auth/reset-password", { token, newPassword }),
  changePassword: (currentPassword, newPassword) => API.post("/auth/change-password", { currentPassword, newPassword }),
};

// Settings
export const settingsApi = {
  get: () => API.get("/settings"),
  update: (data) => API.put("/settings", data),
};

// Users
export const usersApi = {
  updateProfile: (data) => API.put("/users/profile", data),
  getMembers: (status) => API.get("/users/members", { params: status ? { status } : {} }),
  approveMember: (id) => API.post(`/users/approve/${id}`),
  updateStatus: (id, status) => API.put(`/users/status/${id}`, { status }),
  editMember: (id, data) => API.put(`/users/${id}`, data),
  getMember: (id) => API.get(`/users/${id}`),
  addMember: (data) => API.post("/users/add-member", data),
  payRegistration: (id, data) => API.post(`/users/${id}/pay-registration`, data || {}),
  startProbation: (id) => API.post(`/users/${id}/start-probation`),
  activateMember: (id) => API.post(`/users/${id}/activate`),
  exitMember: (id, data) => API.put(`/users/${id}/exit`, data),
  updateRemarks: (id, remarks) => API.put(`/users/${id}/remarks`, { remarks }),
  checkDuplicate: (phone, name) => API.get("/users/check-duplicate", { params: { phone, name } }),
  importMembers: (members) => API.post("/users/import-members", { members }),
  importRegister: (opts) => API.post("/admin/import-register", opts || {}),
  registerPreview: () => API.get("/admin/register-preview"),
  checkProbation: () => API.post("/users/check-probation"),
  // Linked accounts
  getMyLinked: () => API.get("/users/my-linked"),
  getMemberLinked: (id) => API.get(`/users/${id}/linked`),
  linkAccount: (memberId, linkedMemberId) => API.post(`/users/${memberId}/link`, { linkedMemberId }),
  unlinkAccount: (memberId, linkedMemberId) => API.delete(`/users/${memberId}/link/${linkedMemberId}`),
};

// Bereavements
export const bereavementsApi = {
  list: (status) => API.get("/bereavements", { params: status ? { status } : {} }),
  get: (id) => API.get(`/bereavements/${id}`),
  create: (data) => API.post("/bereavements", data),
  update: (id, data) => API.put(`/bereavements/${id}`, data),
  close: (id) => API.post(`/bereavements/${id}/close`),
};

// Contributions
export const contributionsApi = {
  record: (data) => API.post("/contributions/record", data),
  bulkRecord: (data) => API.post("/contributions/bulk-record", data),
  mockPay: (data) => API.post("/contributions/mock-pay", data),
  stkPush: (data) => API.post("/contributions/stk-push", data),
  stkStatus: (checkoutRequestId) => API.get(`/contributions/stk-status/${checkoutRequestId}`),
  submitReference: (data) => API.post("/contributions/submit-reference", data),
};

// Admin payment tools (treasurer + welfare admin + super admin)
export const paymentsApi = {
  listPending: () => API.get("/admin/pending-payments"),
  confirm: (id, note) => API.post(`/admin/pending-payments/${id}/confirm`, { note }),
  reject: (id, reason) => API.post(`/admin/pending-payments/${id}/reject`, { reason }),
  manualPayment: (data) => API.post("/admin/manual-payment", data),
  bulkPayment: (data) => API.post("/admin/bulk-payment", data),
  recent: () => API.get("/admin/recent-payments"),
};

// System (member-facing — payment method hint)
export const paymentApi = {
  method: () => API.get("/system/payment-method"),
};

// Platform-wide KPIs visible to any authenticated member
export const platformApi = {
  kpis: () => API.get("/platform/kpis"),
  payouts: () => API.get("/platform/payouts"),
  incomeBreakdown: () => API.get("/platform/income-breakdown"),
  activeMembers: () => API.get("/platform/active-members"),
};

// Notifications
export const notificationsApi = {
  list: () => API.get("/notifications"),
  markRead: () => API.post("/notifications/mark-read"),
  unreadCount: () => API.get("/notifications/unread-count"),
  remove: (id) => API.delete(`/notifications/${id}`),
  clearAll: () => API.delete("/notifications"),
  report: (includeArchive = true) => API.get("/notifications/report", { params: { includeArchive } }),
};

// Statements
export const statementsApi = {
  my: () => API.get("/statements/my"),
  forMember: (id) => API.get(`/statements/member/${id}`),
  myPdfUrl: () => `${process.env.REACT_APP_BACKEND_URL}/api/statements/my/pdf`,
  memberPdfUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/statements/member/${id}/pdf`,
  myPdfBlob: () => API.get("/statements/my/pdf", { responseType: "blob" }),
  memberPdfBlob: (id) => API.get(`/statements/member/${id}/pdf`, { responseType: "blob" }),
};

// Treasury
export const treasuryApi = {
  summary: () => API.get("/treasury/summary"),
};

// Directory
export const directoryApi = {
  members: () => API.get("/directory/members"),
  arrears: (bereavementId) => API.get("/directory/arrears", { params: bereavementId ? { bereavementId } : {} }),
};

// Areas
export const areasApi = {
  list: () => API.get("/areas"),
  add: (name) => API.post("/areas", { name }),
  remove: (id) => API.delete(`/areas/${id}`),
};

// Announcements
export const announcementsApi = {
  list: () => API.get("/announcements"),
  create: (data) => API.post("/announcements", data),
  update: (id, data) => API.put(`/announcements/${id}`, data),
  remove: (id) => API.delete(`/announcements/${id}`),
};

// Admin utilities
export const adminApi = {
  emailStatus: () => API.get("/admin/email-status"),
  emailTest: (to) => API.post("/admin/email-test", { to }),
  listAdminUsers: () => API.get("/admin/admin-users"),
  searchMembers: (q) => API.get("/admin/members-searchable", { params: { q } }),
  setRole: (userId, role) => API.put(`/admin/admin-users/${userId}/role`, { role }),
  exportMembersCsv: () => API.get("/admin/export/members.csv", { responseType: "blob" }),
  exportContributionsCsv: () => API.get("/admin/export/contributions.csv", { responseType: "blob" }),
  listStkPayments: (params = {}) => API.get("/admin/payments", { params }),
  getStkPayment: (id) => API.get(`/admin/payments/${id}`),
};

// System / Super Admin — config + integrations
export const systemApi = {
  getConfig: () => API.get("/system/config"),
  saveSection: (section, data) => API.put(`/system/config/${section}`, data),
  listAdminUsers: () => API.get("/system/admin-users"),
  setRole: (userId, role) => API.put(`/system/admin-users/${userId}/role`, { role }),
  testMpesa: () => API.post("/system/test/mpesa", {}),
  testLks: () => API.post("/system/test/lks", {}),
  setPaymentRail: (rail) => API.put("/system/payment-rail", { rail }),
  testSms: (to) => API.post("/system/test/sms", { to }),
  testWhatsapp: (to) => API.post("/system/test/whatsapp", { to }),
  listPendingOtps: () => API.get("/system/pending-otps"),
  regenerateOtp: (pendingId) => API.post(`/system/pending-otps/${pendingId}/regenerate`, {}),
  buildExportBundle: () => API.post("/admin/build-export-bundle", {}),
  downloadExportBundle: () => API.get("/admin/export-bundle", { responseType: "blob" }),
};

// Finance / Banking
export const financeApi = {
  overview: () => API.get("/finance/overview"),
  listTransactions: () => API.get("/finance/bank-transactions"),
  createTransaction: (data) => API.post("/finance/bank-transactions", data),
  deleteTransaction: (id) => API.delete(`/finance/bank-transactions/${id}`),
  listCategories: () => API.get("/finance/expense-categories"),
  createCategory: (data) => API.post("/finance/expense-categories", data),
  updateCategory: (id, data) => API.put(`/finance/expense-categories/${id}`, data),
  deleteCategory: (id) => API.delete(`/finance/expense-categories/${id}`),
  listExpenses: (bereavementId) => API.get("/finance/expenses", { params: bereavementId ? { bereavementId } : {} }),
  createExpense: (data) => API.post("/finance/expenses", data),
  deleteExpense: (id) => API.delete(`/finance/expenses/${id}`),
  listPayouts: (bereavementId) => API.get("/finance/payouts", { params: bereavementId ? { bereavementId } : {} }),
  createPayout: (data) => API.post("/finance/payouts", data),
  deletePayout: (id) => API.delete(`/finance/payouts/${id}`),
  pnl: (bereavementId) => API.get(`/finance/bereavement/${bereavementId}/pnl`),
  report: (period) => API.get("/finance/report", { params: period ? { period } : {} }),
  outstandingBreakdown: () => API.get("/finance/outstanding-breakdown"),
};

// Web Push
export const pushApi = {
  publicKey: () => API.get("/push/public-key"),
  subscribe: (sub) => API.post("/push/subscribe", sub),
  unsubscribe: (endpoint) => API.post("/push/unsubscribe", { endpoint }),
  test: (data) => API.post("/push/test", data || {}),
};

// Bulk reminders
export const remindersApi = {
  forBereavement: (bereavementId, opts) =>
    API.post(`/reminders/bereavement/${bereavementId}`, opts || {}),
  history: (limit) => API.get("/reminders/history", { params: limit ? { limit } : {} }),
};

// Wallet
export const walletApi = {
  me: () => API.get("/wallet/me"),
  transactions: (limit) => API.get("/wallet/me/transactions", { params: limit ? { limit } : {} }),
  topup: (amount, phone) => API.post("/wallet/topup", { amount, phone: phone || undefined }),
  toggleAutoPay: (enabled) => API.post("/wallet/autopay/toggle", { enabled }),
  pay: (bereavementId) => API.post(`/wallet/pay/${bereavementId}`),
  sweep: () => API.post("/wallet/sweep"),
  // Admin
  adminList: () => API.get("/admin/wallets"),
  adminMemberTxns: (userId, limit) => API.get(`/admin/wallets/${userId}/transactions`, { params: limit ? { limit } : {} }),
};

// Arrears (member-facing) + multi-bereavement STK
export const arrearsApi = {
  mine: () => API.get("/contributions/my-arrears"),
  stkPushMulti: (bereavementIds, phone) =>
    API.post("/contributions/stk-push-multi", { bereavementIds, phone: phone || undefined }),
};

// Admin tools — password reset + bulk-by-member + login activity + STK rail health
export const adminToolsApi = {
  resetPasswordToDefault: (memberId) => API.post(`/users/${memberId}/reset-password-default`),
  memberArrears: (memberId) => API.get(`/admin/members/${memberId}/arrears`),
  bulkByMember: (payload) => API.post("/admin/bulk-by-member", payload),
  loginActivity: () => API.get("/admin/login-activity"),
  stkHealth: (hours, limit) => API.get("/admin/stk-health", { params: { hours: hours || 24, limit: limit || 50 } }),
};

export default API;
